import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FaArrowLeft, FaPlay, FaCheckCircle, FaTimesCircle, FaUserGraduate, FaTools } from "react-icons/fa";
import { API_BASE_URL } from "../../config/apiConfig";
import { MathText, MathJaxContext, mathJaxConfig } from "../../components/MathText";
import { formatAiText } from "../../utils/formatAiText";
import { authFetch } from "../../utils/api/authFetch";
import { SegmentViewer } from "../../components/marketplace";
import { ProtectedArea } from "../../components/protected";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });
const authHeadersJson = () => ({ "Content-Type": "application/json", ...authHeaders() });

const LessonDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // The teacher's "Preview as a student" opens this very page rather than a
    // mock-up, so what they check is what students actually get — including
    // drafts, which the API serves to the owner. `?preview=1` only changes the
    // way back (to the builder, not the student lesson list) and adds the banner;
    // the API refuses to record the owner's run against their own lesson.
    const [searchParams] = useSearchParams();
    const isPreview = searchParams.get("preview") === "1";
    const backToBuilder = () => navigate(`/teacher/lessons/${id}/build`);
    const [lesson, setLesson] = useState(null);
    const [segments, setSegments] = useState([]);
    const [flashcards, setFlashcards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("lesson"); // lesson | flashcards | practice

    const [practiceQuestions, setPracticeQuestions] = useState([]);
    const [testId, setTestId] = useState(null);
    const [current, setCurrent] = useState(0);
    const [answers, setAnswers] = useState({});
    const [finished, setFinished] = useState(false);

    useEffect(() => {
        const fetchLesson = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/lessons/${id}`, { headers: authHeaders() });
                const data = await res.json();
                if (!res.ok) throw new Error(data.message || "Lesson not found");
                setLesson(data.data.lesson);
                setSegments(data.data.segments || []);
                setFlashcards(data.data.flashcards || []);
            } catch (err) {
                toast.error(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchLesson();
    }, [id]);

    const startPractice = async () => {
        try {
            // authFetch refreshes an expired token and retries once, so practice
            // no longer fails just because the access token lapsed mid-session.
            //
            // These used to run in parallel. The test is now created AFTER the
            // questions are counted, so the sitting records how many it asked —
            // a practice run left half-finished is then scored out of the whole
            // set rather than only the part that was answered.
            const questionsRes = await authFetch(`/api/lessons/${id}/practice`);
            if (questionsRes.status === 401 || questionsRes.status === 403) {
                toast.error("Your session expired. Please log in again and retry.");
                return;
            }
            const questionsData = await questionsRes.json().catch(() => ({}));
            if (!questionsData.data?.length) {
                toast.error("No practice questions are ready for this lesson yet.");
                return;
            }

            const testRes = await authFetch(
                `/api/tests?total_questions=${questionsData.data.length}`
            );
            if (testRes.status === 401 || testRes.status === 403) {
                toast.error("Your session expired. Please log in again and retry.");
                return;
            }
            const testData = await testRes.json().catch(() => ({}));
            if (!testData.data?.id) {
                toast.error("Couldn't start a practice session just now. Please try again.");
                return;
            }
            setPracticeQuestions(questionsData.data);
            setTestId(testData.data.id);
            setCurrent(0);
            setAnswers({});
            setFinished(false);
            setView("practice");
        } catch (err) {
            toast.error("Could not start practice");
        }
    };

    const selectOption = async (question, letter) => {
        if (answers[question.id]) return; // already answered
        setAnswers((prev) => ({ ...prev, [question.id]: letter }));
        try {
            await fetch(`${API_BASE_URL}/api/answer`, {
                method: "POST",
                headers: authHeadersJson(),
                body: JSON.stringify({
                    question_id: question.id,
                    selected_option: letter,
                    test_id: testId,
                    mode: "lesson_practice",
                    topic: question.topic,
                    subject: question.subject,
                    answerType: "personal",
                }),
            });
        } catch (err) {
            // Answer is still shown locally even if the network call fails.
        }
    };

    const finishPractice = async () => {
        try {
            await fetch(`${API_BASE_URL}/api/tests/${testId}/submit`, { method: "PATCH", headers: authHeaders() });
            await fetch(`${API_BASE_URL}/api/lessons/${id}/complete`, {
                method: "POST",
                headers: authHeadersJson(),
                body: JSON.stringify({ test_id: testId }),
            });
            setFinished(true);
        } catch (err) {
            toast.error("Could not save your practice results");
        }
    };

    if (loading) return <div className="p-10 text-gray-500">Loading...</div>;
    if (!lesson) return <div className="p-10 text-gray-500">Lesson not found.</div>;

    const correctCount = practiceQuestions.filter((q) => answers[q.id] === q.correctAnswer).length;

    return (
        <MathJaxContext config={mathJaxConfig}>
            <div className="min-h-screen bg-gray-50">
                <nav className="bg-[#2148C0] shadow-md px-6 py-4 flex items-center gap-4">
                    <button
                        onClick={() => {
                            if (view !== "lesson") { setView("lesson"); return; }
                            isPreview ? backToBuilder() : navigate("/lessons");
                        }}
                        className="text-white"
                    >
                        <FaArrowLeft size={18} />
                    </button>
                    <h1 className="text-xl font-bold text-white">{lesson.title}</h1>
                </nav>

                {isPreview && (
                    <div className="bg-amber-50 border-b border-amber-200 px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3 flex-wrap">
                        <p className="text-sm text-amber-800 flex items-center gap-2">
                            <FaUserGraduate className="shrink-0" size={13} />
                            <span>
                                <span className="font-semibold">Student preview</span> — this is exactly what your students see
                                {lesson.status !== "published" && <span className="font-semibold"> (not published yet)</span>}.
                                Practice runs here aren't recorded.
                            </span>
                        </p>
                        <button onClick={backToBuilder}
                            className="text-amber-900 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded-lg px-3 py-1.5 text-sm font-semibold flex items-center gap-2 shrink-0">
                            <FaTools size={12} /> Back to editing
                        </button>
                    </div>
                )}

                <div className="max-w-3xl mx-auto p-6">
                    {view === "lesson" && (
                        <div className="space-y-5">
                            {lesson.body && (
                                <div className="bg-white rounded-xl shadow-sm p-6">
                                    <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{lesson.body}</p>
                                </div>
                            )}
                            {segments.length > 0 && (
                                // Read-only: watermarked, no download, no copy/print.
                                <ProtectedArea contentClassName="space-y-5">
                                    {segments.map((seg, idx) => (
                                        <SegmentViewer key={seg.id} segment={seg} index={idx} />
                                    ))}
                                </ProtectedArea>
                            )}
                            {!lesson.body && segments.length === 0 && (
                                <div className="bg-white rounded-xl shadow-sm p-6 text-sm text-gray-500">This lesson has no content yet.</div>
                            )}
                            {lesson.ai_summary && (
                                <div className="bg-blue-50 rounded-xl p-6">
                                    <h3 className="font-semibold text-blue-800 mb-2">Key Concepts</h3>
                                    <p className="text-sm text-blue-900 whitespace-pre-wrap">{formatAiText(lesson.ai_summary)}</p>
                                </div>
                            )}
                            <div className="flex gap-3">
                                {flashcards.length > 0 && (
                                    <button onClick={() => setView("flashcards")} className="bg-white border rounded-lg px-4 py-2 font-semibold text-gray-700">
                                        Flashcards ({flashcards.length})
                                    </button>
                                )}
                                <button onClick={startPractice} className="bg-blue600 text-white rounded-lg px-4 py-2 font-semibold flex items-center gap-2">
                                    <FaPlay /> Take Practice
                                </button>
                            </div>
                        </div>
                    )}

                    {view === "flashcards" && (
                        <div className="grid grid-cols-2 gap-3">
                            {flashcards.map((f) => (
                                <div key={f.id} className="bg-white rounded-xl shadow-sm p-4">
                                    <p className="font-medium text-gray-800 text-sm">{f.front}</p>
                                    <p className="text-sm text-gray-500 mt-2 border-t pt-2">{f.back}</p>
                                </div>
                            ))}
                        </div>
                    )}

                    {view === "practice" && !finished && practiceQuestions[current] && (
                        <div className="bg-white rounded-xl shadow-sm p-6">
                            <p className="text-xs text-gray-400 mb-2">Question {current + 1} of {practiceQuestions.length}</p>
                            <MathText text={practiceQuestions[current].question} className="font-medium text-gray-800 block mb-4" />
                            <div className="space-y-2">
                                {["A", "B", "C", "D"].map((letter) => {
                                    const q = practiceQuestions[current];
                                    const chosen = answers[q.id];
                                    const isChosen = chosen === letter;
                                    const isCorrectLetter = q.correctAnswer === letter;
                                    let style = "border-gray-200 hover:border-blue600";
                                    if (chosen) {
                                        if (isCorrectLetter) style = "border-green-500 bg-green-50";
                                        else if (isChosen) style = "border-red-500 bg-red-50";
                                    }
                                    return (
                                        <button key={letter} onClick={() => selectOption(q, letter)}
                                            className={`w-full text-left border rounded-lg p-3 flex items-center justify-between ${style}`}>
                                            <span><span className="font-semibold mr-2">{letter}.</span><MathText text={q[`option_${letter.toLowerCase()}`]} /></span>
                                            {chosen && isCorrectLetter && <FaCheckCircle className="text-green-500" />}
                                            {chosen && isChosen && !isCorrectLetter && <FaTimesCircle className="text-red-500" />}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="flex justify-between mt-6">
                                <button disabled={current === 0} onClick={() => setCurrent((c) => c - 1)} className="text-gray-500 disabled:opacity-30">Previous</button>
                                {current < practiceQuestions.length - 1 ? (
                                    <button onClick={() => setCurrent((c) => c + 1)} className="bg-blue600 text-white px-4 py-2 rounded-lg font-semibold">Next</button>
                                ) : (
                                    <button onClick={finishPractice} className="bg-green-600 text-white px-4 py-2 rounded-lg font-semibold">Finish</button>
                                )}
                            </div>
                        </div>
                    )}

                    {view === "practice" && finished && (
                        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                            <h3 className="text-2xl font-bold text-gray-800 mb-2">
                                {correctCount}/{practiceQuestions.length} correct
                            </h3>
                            <p className="text-gray-500 mb-6">
                                {isPreview
                                    ? "This is the screen your students land on. Nothing was recorded for the preview."
                                    : "Great work — this feeds into your Study Plan and Readiness score."}
                            </p>
                            <button onClick={() => setView("lesson")} className="bg-blue600 text-white px-5 py-2 rounded-lg font-semibold">Back to Lesson</button>
                        </div>
                    )}
                </div>
            </div>
        </MathJaxContext>
    );
};

export default LessonDetail;
